import axios, { type AxiosRequestConfig } from "axios";
import { trackEvent } from "./analytics";
import {
  TOKEN_LENGTH_WARNING_CHARS,
  describeConnection,
  describeToken,
  estimateHeaderBytes,
  isTabVisible,
  readConnection,
  tailOf,
} from "./diagnosticsContext";

export const DIAGNOSTIC_EVENTS = [
  "devices_load_failed",
  "devices_load_recovered",
  "devices_empty",
  "devices_filtered_to_zero",
  "devices_retry_clicked",
  "socket_connect_error",
  "socket_gave_up",
  "socket_server_disconnect",
  "session_expired",
  "login_failed",
  "api_error",
  "token_oversized",
] as const;

export type DiagnosticEvent = (typeof DIAGNOSTIC_EVENTS)[number];

type DiagnosticValue = string | number | boolean;
export type DiagnosticProps = Record<
  string,
  DiagnosticValue | null | undefined
>;

const MAX_PARAM_LENGTH = 100;
const DEDUPE_WINDOW_MS = 60_000;

const lastTrackedAt = new Map<string, number>();

const readBackendMeta = (
  data: unknown,
): { messageKey?: string; message?: string } => {
  if (typeof data !== "object" || data === null || !("meta" in data)) {
    return {};
  }
  const meta = data.meta;
  if (typeof meta !== "object" || meta === null) {
    return {};
  }
  return {
    messageKey:
      "messageKey" in meta && typeof meta.messageKey === "string"
        ? meta.messageKey
        : undefined,
    message:
      "message" in meta && typeof meta.message === "string"
        ? meta.message
        : undefined,
  };
};

const requestPathAndQuery = (
  config: AxiosRequestConfig | undefined,
): string | undefined => {
  if (!config) return undefined;
  try {
    return axios.getUri(config).replace(/^https?:\/\/[^/]+/, "");
  } catch {
    return config.url;
  }
};

const knownHeaderBytes = (pathAndQuery: string | undefined): number => {
  const token = localStorage.getItem("token");
  return estimateHeaderBytes([
    token ? `Bearer ${token}` : null,
    localStorage.getItem("sessionId"),
    localStorage.getItem("userId"),
    localStorage.getItem("companyId"),
    pathAndQuery,
    navigator.userAgent,
  ]);
};

export const describeAxiosError = (error: unknown): DiagnosticProps => {
  if (!axios.isAxiosError(error)) {
    return {
      error_message: error instanceof Error ? error.message : String(error),
    };
  }
  const meta = readBackendMeta(error.response?.data);
  const startedAt = (error.config as { requestStartedAt?: number } | undefined)
    ?.requestStartedAt;
  const pathAndQuery = requestPathAndQuery(error.config);
  return {
    endpoint: error.config?.url?.split("?")[0],
    http_status: error.response?.status,
    message_key: meta.messageKey,
    backend_message: meta.message,
    axios_code: error.code,
    online: navigator.onLine,
    duration_ms: startedAt ? Date.now() - startedAt : undefined,
    url_length: pathAndQuery?.length,
    header_bytes_known: knownHeaderBytes(pathAndQuery),
  };
};

const sessionProps = (): DiagnosticProps => ({
  selected_company_id: localStorage.getItem("companyId"),
  user_company_id: localStorage.getItem("userCompanyId"),
  role_id: localStorage.getItem("userRoleId"),
  session_id_tail: tailOf(localStorage.getItem("sessionId")),
  tab_visible: isTabVisible(),
  ...describeToken(localStorage.getItem("token")),
  ...describeConnection(readConnection()),
});

const toEventParams = (props: DiagnosticProps) =>
  Object.entries(props).reduce<Record<string, DiagnosticValue>>(
    (acc, [key, value]) => {
      if (value === null || value === undefined || value === "") return acc;
      acc[key] =
        typeof value === "string" ? value.slice(0, MAX_PARAM_LENGTH) : value;
      return acc;
    },
    {},
  );

export const trackDiagnostic = (
  event: DiagnosticEvent,
  props: DiagnosticProps = {},
  dedupeKey?: string,
) => {
  if (dedupeKey) {
    const now = Date.now();
    const last = lastTrackedAt.get(dedupeKey);
    if (last && now - last < DEDUPE_WINDOW_MS) return;
    lastTrackedAt.set(dedupeKey, now);
  }
  void trackEvent(event, toEventParams({ ...sessionProps(), ...props }));
};

export const trackTokenWeight = (token: string) => {
  if (token.length <= TOKEN_LENGTH_WARNING_CHARS) return;
  trackDiagnostic("token_oversized", { token_length: token.length }, "token_oversized");
};
